import React, { useState, useEffect } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { formatDateForInput } from '../utils/helpers';

function TransactionForm({ transaction, onClose }) {
  const { state, addTransaction, updateTransaction, addCategory } = useFinance();
  const [activeTab, setActiveTab] = useState('expense'); // expense, income, transfer
  
  const [form, setForm] = useState({
    amount: '',
    description: '',
    categoryId: '',
    modeId: state.modes && state.modes[0] ? state.modes[0].id : '',
    targetModeId: '', // For transfers
    tags: [],
    date: formatDateForInput(new Date().toISOString()),
  });

  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  


  // Initialize form if editing
  useEffect(() => {
    if (transaction) {
      setActiveTab(transaction.type);
      setForm({
        amount: transaction.amount.toString(),
        description: transaction.description,
        categoryId: transaction.categoryId || '',
        modeId: transaction.modeId || (state.modes[0] ? state.modes[0].id : ''),
        targetModeId: transaction.targetModeId || '',
        tags: transaction.tags || [],
        date: formatDateForInput(transaction.date),
      });
    } else {
      // Set defaults for new transaction
      const defaultMode = state.modes && state.modes[0] ? state.modes[0].id : '';
      setForm(prev => ({ ...prev, modeId: defaultMode }));
    }
  }, [transaction, state.modes]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (activeTab === 'transfer' && form.modeId === form.targetModeId) {
      alert("Source and Target modes must be different for a transfer.");
      return;
    }
    
    const data = {
      ...form,
      type: activeTab,
      amount: parseFloat(form.amount),
      date: new Date(form.date).toISOString(),
    };
    
    // Clean up fields based on type
    if (activeTab === 'transfer') {
      // Transfers use a specific category or none. Let's auto-assign Transfer category if exists
      const transferCat = state.categories.find(c => c.name === 'Transfer');
      if (transferCat) data.categoryId = transferCat.id;
    } else {
      data.targetModeId = null;
    }

    if (transaction) {
      updateTransaction({ ...transaction, ...data });
    } else {
      addTransaction(data);
    }
    onClose();
  };

  const handleCreateCategory = (e) => {
    e.preventDefault();
    if (newCategoryName.trim()) {
      const newCat = { name: newCategoryName, icon: '🆕', color: '#64748b' };
      addCategory(newCat);
      setNewCategoryName('');
      setCategoryModalOpen(false);
    }
  };

  const toggleTag = (tagId) => {
    setForm(prev => {
      const tags = prev.tags.includes(tagId) 
        ? prev.tags.filter(t => t !== tagId)
        : [...prev.tags, tagId];
      return { ...prev, tags };
    });
  };

  // Get relevant categories based on type (income/expense generally share, but logically some are income-only)
  // For simplicity, showing all.
  const categories = state.categories;

  return (
    <div className="space-y-6">
      {/* Type Toggle */}
      <div className="flex bg-slate-700/50 p-1 rounded-xl">
        <button
          type="button"
          onClick={() => setActiveTab('expense')}
          className={`flex-1 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'expense' 
              ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/25' 
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Expense
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('income')}
          className={`flex-1 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'income' 
              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25' 
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Income
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('transfer')}
          className={`flex-1 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'transfer' 
              ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' 
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          Transfer
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Amount</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">{state.currency}</span>
            <input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              autoFocus
              className="w-full pl-10 pr-4 py-4 bg-slate-700/50 border border-slate-600 rounded-xl text-3xl font-bold text-white placeholder-slate-500 focus:outline-none focus:border-primary-500 transition-all"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Payment Modes */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {activeTab === 'transfer' ? 'From' : 'Payment Mode'}
            </label>
            <select
              value={form.modeId}
              onChange={(e) => setForm({ ...form, modeId: e.target.value })}
              required
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
            >
              {state.modes.map(mode => (
                <option key={mode.id} value={mode.id}>{mode.icon} {mode.name}</option>
              ))}
            </select>
          </div>
          
          {activeTab === 'transfer' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">To</label>
              <select
                value={form.targetModeId}
                onChange={(e) => setForm({ ...form, targetModeId: e.target.value })}
                required
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
              >
                <option value="">Select Target</option>
                {state.modes
                  .filter(m => m.id !== form.modeId)
                  .map(mode => (
                  <option key={mode.id} value={mode.id}>{mode.icon} {mode.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            required
            className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-primary-500 transition-all"
            placeholder="What's this for?"
          />
        </div>

        {/* Categories (Hidden for Transfer) */}
        {activeTab !== 'transfer' && (
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setForm({ ...form, categoryId: cat.id })}
                  className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all ${
                    form.categoryId === cat.id
                      ? 'bg-slate-700 border-primary-500 ring-1 ring-primary-500'
                      : 'bg-slate-800/50 border-slate-700 hover:bg-slate-700 hover:border-slate-500'
                  }`}
                >
                  <span className="text-2xl mb-1">{cat.icon}</span>
                  <span className="text-[10px] text-slate-300 truncate w-full text-center">{cat.name}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCategoryModalOpen(true)}
                className="flex flex-col items-center justify-center p-2 rounded-xl border border-dashed border-slate-600 hover:border-slate-400 text-slate-400 hover:text-white transition-colors"
              >
                <span className="text-2xl mb-1">➕</span>
                <span className="text-[10px]">Add New</span>
              </button>
            </div>
            {/* Fallback hidden select for validation if needed, or visual feedback above is enough */}
            <input 
              type="text" 
              required 
              value={form.categoryId || ''} 
              className="sr-only" 
              onChange={() => {}}
              onInvalid={e => e.target.setCustomValidity('Please select a category')}
            />
          </div>
        )}

        {/* Tags, Date, Buttons */}
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-300 mb-2">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              required
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
        
        {/* Tags Section */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Tags</label>
          <div className="flex flex-wrap gap-2">
            {state.tags.map(tag => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  form.tags.includes(tag.id)
                    ? 'ring-2 ring-offset-2 ring-offset-slate-900 scale-105'
                    : 'opacity-70 hover:opacity-100'
                }`}
                style={{ 
                  backgroundColor: form.tags.includes(tag.id) ? tag.color : `${tag.color}20`,
                  color: form.tags.includes(tag.id) ? '#fff' : tag.color,
                  borderColor: tag.color
                }}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-4 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white font-bold text-lg hover:from-primary-500 hover:to-primary-400 shadow-lg shadow-primary-500/25 transition-all transform hover:-translate-y-0.5 active:translate-y-0"
        >
          {transaction ? 'Update Transaction' : 'Add Transaction'}
        </button>
      </form>
      
      {/* Mini Modal for New Category */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-sm border border-slate-700 shadow-2xl animate-fade-in">
            <h3 className="text-lg font-bold text-white mb-4">Add Category</h3>
            <div className="space-y-4">
              <input
                type="text"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
                placeholder="Category Name"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:border-primary-500"
                autoFocus
              />
              <div className="flex gap-3">
                <button onClick={() => setCategoryModalOpen(false)} className="flex-1 py-2 text-slate-400 hover:text-white">Cancel</button>
                <button onClick={handleCreateCategory} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-500">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionForm;
